"use client";

import { Facebook, Instagram, Loader, Twitter, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useState } from "react";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [inquiryType, setInquiryType] = useState("general");
  const inquiryOptions = [{
    value: "general",
    label: "GENERAL"
  }, {
    value: "individual",
    label: "INDIVIDUAL"
  }, {
    value: "family",
    label: "FAMILY"
  }, {
    value: "couples",
    label: "COUPLES"
  }];
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-start justify-center h-full gap-6 p-8">
        <div className="text-[11px] tracking-[0.3em] font-default font-medium text-primary">/ MESSAGE RECEIVED</div>
        <h3 className="text-3xl md:text-4xl font-default font-bold leading-[0.95]">{successMessage}</h3>
        <p className="font-default text-foreground/70 text-base italic">I have received your request. I will reach out within one business day.</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 h-12 px-6 text-[11px] tracking-[0.3em] font-default font-medium border-foreground/20 hover:border-primary hover:text-primary bg-transparent rounded-md">
          SEND ANOTHER →
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="flex flex-col h-full gap-7">
      <div className="flex flex-col gap-4">
        <div className="text-[11px] tracking-[0.3em] font-default font-medium text-primary">/ SERVICE TYPE</div>
        <ToggleGroup type="single" value={inquiryType} onValueChange={v => v && setInquiryType(v)} className="grid grid-cols-2 md:grid-cols-4 gap-0 w-full rounded-md overflow-hidden border border-foreground/15">
          {inquiryOptions.map((opt, i) => <ToggleGroupItem data-index={i} key={opt.value} value={opt.value} className="h-12 text-[11px] tracking-[0.25em] font-default font-medium text-foreground/60 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground hover:text-foreground border-0 rounded-md">
              {opt.label}
            </ToggleGroupItem>)}
        </ToggleGroup>
      </div>

      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="text-[11px] tracking-[0.3em] font-default font-medium text-primary uppercase">/ Your Name</FormLabel>
            <FormControl>
              <Input placeholder="Jane Doe" className="h-12 bg-transparent border-0 border-b border-foreground/20 px-0 text-base font-default focus-visible:border-primary focus-visible:ring-0 placeholder:text-foreground/30" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="text-[11px] tracking-[0.3em] font-default font-medium text-primary uppercase">/ Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@example.com" className="h-12 bg-transparent border-0 border-b border-foreground/20 px-0 text-base font-default focus-visible:border-primary focus-visible:ring-0 placeholder:text-foreground/30" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="text-[11px] tracking-[0.3em] font-default font-medium text-primary uppercase">/ Phone Number</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="407-376-2963" className="h-12 bg-transparent border-0 border-b border-foreground/20 px-0 text-base font-default focus-visible:border-primary focus-visible:ring-0 placeholder:text-foreground/30" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="insurance_information" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="text-[11px] tracking-[0.3em] font-default font-medium text-primary uppercase">/ Insurance Information</FormLabel>
            <FormControl>
              <Input type="text" placeholder="e.g., BlueCross BlueShield" className="h-12 bg-transparent border-0 border-b border-foreground/20 px-0 text-base font-default focus-visible:border-primary focus-visible:ring-0 placeholder:text-foreground/30" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Min 10 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-2 flex-1 flex flex-col">
            <FormLabel className="text-[11px] tracking-[0.3em] font-default font-medium text-primary uppercase">/ How can I help?</FormLabel>
            <FormControl>
              <Textarea placeholder="Share a little about what you're going through..." rows={6} className="flex-1 min-h-[140px] bg-transparent border-0 border-b border-foreground/20 px-0 py-2 text-base font-default focus-visible:border-primary focus-visible:ring-0 placeholder:text-foreground/30 resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10 rounded-md">
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      <div className="flex flex-col gap-3 mt-auto">
        <Button type="submit" disabled={isSubmitting} className="w-full h-16 text-[12px] tracking-[0.35em] font-default font-bold bg-primary text-primary-foreground hover:bg-primary/90 rounded-md shadow-2xl">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              SENDING...
            </> : "REQUEST CONSULTATION →"}
        </Button>
        <p className="text-xs font-default italic text-foreground/50 text-center">Free consultations available.</p>
      </div>
    </Form>;
}
export default function TakeTheNextStepTogether() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const infoBlocks = [{
    eyebrow: "OFFICE LOCATION",
    lines: ["Virtual\xA0", "Serving Florida Clients"],
    sub: "Telehealth\xA0"
  }, {
    eyebrow: "CONTACT DIRECTLY",
    lines: ["tanyarp@hotmail.com"],
    sub: "I reply within one business day"
  }, {
    eyebrow: "CALL FOR SUPPORT",
    lines: ["407-376-2963"],
    sub: "Available for your healing journey"
  }];
  const socials = [{
    icon: Instagram,
    label: "Instagram",
    href: "#"
  }, {
    icon: Twitter,
    label: "Twitter",
    href: "#"
  }, {
    icon: Facebook,
    label: "Facebook",
    href: "#"
  }, {
    icon: Youtube,
    label: "Youtube",
    href: "#"
  }];
  return <section data-section-id="3460" id="take-the-next-step-together" className="dark bg-background text-foreground relative overflow-hidden pt-32 md:pt-48 lg:pt-[200px] pb-24 md:pb-32 lg:pb-[140px] px-6 md:px-12 lg:px-20">
      <div ref={ref} className={`mx-auto max-w-[1440px] transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 flex flex-col">
            <div className="text-[12px] tracking-[0.3em] font-default font-medium text-foreground/50 mb-1">
              / <span className="text-primary">GET IN TOUCH</span>
            </div>
            <div className="w-px h-16 bg-primary mt-2 mb-10 ml-1" />

            <h2 className="font-default font-black text-5xl sm:text-6xl md:text-7xl lg:text-[96px] leading-[0.9] tracking-tight mb-8">Take the&nbsp;<br />next step&nbsp;<br /><span className="italic" data-ast-id="f:/src/pages/Home/sections/TakeTheNextStepTogether.tsx|sig:3br0c1" data-ast-start="9532" data-ast-end="9718" style={{
              WebkitTextStroke: '1.5px currentcolor',
              color: 'transparent'
            }}>together.</span></h2>

            <p className="font-default text-base md:text-[18px] leading-relaxed text-foreground/70 max-w-[420px] mb-12">
              You deserve to be heard. Whether you are struggling with anxiety, trauma, or life transitions, I am here to help you find the balance you are looking for.
            </p>

            <div className="flex flex-col">
              {infoBlocks.map((block, i) => <div data-index={i} key={block.eyebrow} className="py-6 border-t border-foreground/15 first:border-t-0 first:pt-0 last:pb-8">
                  <div className="text-[11px] tracking-[0.3em] font-default font-medium text-primary mb-3">
                    {block.eyebrow}
                  </div>
                  {block.lines.map((line, j) => <div data-index={j} key={j} className="font-default text-[17px] text-foreground leading-snug">
                      {line}
                    </div>)}
                  <div className="font-default italic text-[14px] text-foreground/50 mt-2">{block.sub}</div>
                </div>)}
            </div>

            
          </div>

          <div className="lg:col-span-7">
            <div className="relative backdrop-blur-xl bg-card/60 border border-foreground/15 rounded-md p-8 md:p-12 lg:p-14 shadow-4xl overflow-hidden" style={{
            boxShadow: "0 48px 96px -24px rgba(0,0,0,0.85), inset 0 1px 0 0 rgba(255,255,255,0.04)"
          }}>
              <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[radial-gradient(circle_at_20%_30%,_var(--primary)_0%,_transparent_50%),radial-gradient(circle_at_80%_70%,_var(--foreground)_0%,_transparent_50%)]" />

              <div className="relative z-10 h-full min-h-[680px] flex flex-col">
                <FormProvider formKey="next_step_form_home" formId="next_step_form_home" sectionName="take-the-next-step-together" defaultValues={{
                name: "",
                email: "",
                phone: "",
                insurance_information: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {},
                insurance_information: {
                  required: true
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="REQUEST CONSULTATION →" successMessage="Your message has been received." errorMessage="Please try sending again." className="flex flex-col h-full">
                  <ContactForm />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}